import { useCallback, useEffect, useRef, useState } from "react";
import SheetChrome from "./SheetChrome";
import useBackButton from "../../hooks/useBackButton";

const SHEET_MOVE_THRESHOLD = 3;
const SHEET_CLOSE_THRESHOLD = 18;

export default function BottomSheet({
  title,
  label,
  onClose,
  children,
  className = "",
  bodyClassName = "",
  disableDrag = false,
  disableBackdropClose = false,
  dragHandleOnly = true,
}) {
  const sheetRef = useRef(null);
  const dragHandleRef = useRef(null);
  const dragStartY = useRef(null);
  const dragOffsetRef = useRef(0);
  const dragMovedRef = useRef(false);
  const handleTouchActiveRef = useRef(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);

  const setSheetOffset = useCallback((value) => {
    dragOffsetRef.current = value;
    setDragOffset(value);
  }, []);

  const closeWithSlide = useCallback(() => {
    setDragging(false);
    setSheetOffset(window.innerHeight);
    window.setTimeout(onClose, 160);
  }, [onClose, setSheetOffset]);

  useBackButton(closeWithSlide);

  const beginDrag = useCallback((clientY) => {
    if (disableDrag) return;
    dragStartY.current = clientY;
    dragOffsetRef.current = 0;
    dragMovedRef.current = false;
    setDragging(true);
  }, [disableDrag]);

  const moveDrag = useCallback((clientY, event) => {
    if (disableDrag || dragStartY.current == null) return;
    const nextOffset = Math.max(0, clientY - dragStartY.current);
    if (nextOffset > SHEET_MOVE_THRESHOLD) dragMovedRef.current = true;
    setSheetOffset(nextOffset);
    if (nextOffset > SHEET_MOVE_THRESHOLD) event?.preventDefault?.();
  }, [disableDrag, setSheetOffset]);

  const endDrag = useCallback(() => {
    if (dragStartY.current == null) return;
    dragStartY.current = null;
    if (dragOffsetRef.current > SHEET_CLOSE_THRESHOLD) {
      closeWithSlide();
      return;
    }
    setDragging(false);
    setSheetOffset(0);
  }, [closeWithSlide, setSheetOffset]);

  useEffect(() => {
    const dragTarget = dragHandleOnly ? dragHandleRef.current : sheetRef.current;
    if (!dragTarget || disableDrag) return undefined;

    function handleTouchStart(event) {
      const touch = event.touches?.[0];
      if (!touch) return;
      handleTouchActiveRef.current = true;
      beginDrag(touch.clientY);
    }
    function handleTouchMove(event) {
      const touch = event.touches?.[0];
      if (touch) moveDrag(touch.clientY, event);
    }
    function handleTouchEnd() {
      if (!handleTouchActiveRef.current) return;
      handleTouchActiveRef.current = false;
      const shouldTapClose = dragHandleOnly && !dragMovedRef.current;
      endDrag();
      if (shouldTapClose) closeWithSlide();
    }
    function handleTouchCancel() {
      handleTouchActiveRef.current = false;
      endDrag();
    }

    dragTarget.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
    window.addEventListener("touchcancel", handleTouchCancel);

    return () => {
      dragTarget.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchCancel);
    };
  }, [beginDrag, closeWithSlide, disableDrag, dragHandleOnly, endDrag, moveDrag]);

  return (
    <div
      className="fd-mobile-sheet-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={label || title}
      onClick={event => event.stopPropagation()}
    >
      <button
        type="button"
        className="fd-mobile-sheet-scrim"
        onClick={disableBackdropClose ? undefined : closeWithSlide}
        aria-label={disableBackdropClose ? undefined : `Close ${title}`}
        tabIndex={disableBackdropClose ? -1 : 0}
      />
      <aside
        ref={sheetRef}
        className={`fd-mobile-sheet fd-mobile-sheet--food${className ? ` ${className}` : ""}${disableDrag ? " fd-mobile-sheet--locked" : ""}${dragging ? " is-dragging" : ""}`}
        style={{ transform: dragOffset ? `translateY(${dragOffset}px)` : undefined }}
        onPointerDown={event => {
          if (disableDrag || dragHandleOnly) return;
          if (event.pointerType === "mouse" && event.button !== 0) return;
          event.currentTarget.setPointerCapture?.(event.pointerId);
          beginDrag(event.clientY);
        }}
        onPointerMove={event => !disableDrag && !dragHandleOnly && moveDrag(event.clientY, event)}
        onPointerUp={!disableDrag && !dragHandleOnly ? endDrag : undefined}
        onPointerCancel={!disableDrag && !dragHandleOnly ? endDrag : undefined}
      >
        <SheetChrome
          title={title}
          onClose={closeWithSlide}
          bodyClassName={bodyClassName}
          handleRef={dragHandleRef}
          handleProps={{
            onPointerDown: event => {
              if (disableDrag) return;
              if (event.pointerType === "mouse" && event.button !== 0) return;
              event.currentTarget.setPointerCapture?.(event.pointerId);
              beginDrag(event.clientY);
            },
            onPointerMove: event => !disableDrag && moveDrag(event.clientY, event),
            onPointerUp: !disableDrag ? event => {
              event.currentTarget.releasePointerCapture?.(event.pointerId);
              const shouldTapClose = !dragMovedRef.current;
              endDrag();
              if (shouldTapClose) closeWithSlide();
            } : undefined,
            onPointerCancel: !disableDrag ? event => {
              event.currentTarget.releasePointerCapture?.(event.pointerId);
              endDrag();
            } : undefined,
          }}
        >
          {children}
        </SheetChrome>
      </aside>
    </div>
  );
}
